/*
 * See the file LICENSE for the full license governing this code.
 */

import {
  QueryClient,
  useMutation as useMutationReactQuery,
  useQuery as useQueryReactQuery,
} from 'react-query';
import { useErrorHandler } from 'react-error-boundary';
import axios from 'axios';
import { DEFAULT_NETWORK_ERROR_MESSAGE } from '../constants';

export class AuthenticateError extends Error {}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (n, error) => {
        if (error instanceof AuthenticateError) {
          return false;
        }
        return n <= 3;
      },
    },
  },
});

let axiosInstance = axios;

export const createAxiosInstance = (axiosConfig) => {
  axiosInstance = axios.create(axiosConfig);
};

export function useQuery(queryKey, queryFn, options) {
  const res = useQueryReactQuery(queryKey, queryFn, options);
  const errorHandler = useErrorHandler();
  if (res.error instanceof AuthenticateError) {
    errorHandler(res.error);
  }
  return res;
}

export function useMutation(mutationFn, options) {
  const res = useMutationReactQuery(mutationFn, options);
  useErrorHandler(res.error);
  return res;
}

export const networkWithErrorConvert = async (axiosConfig) => {
  try {
    return (await axiosInstance(axiosConfig)).data;
  } catch (error) {
    if (
      error?.response?.status === 401 &&
      !axiosConfig.url.endsWith('/signin')
    ) {
      throw new AuthenticateError(
        error?.response?.data?.error || 'Authenticate error'
      );
    }
    throw new Error(
      error.response?.data?.error || DEFAULT_NETWORK_ERROR_MESSAGE
    );
  }
};

export async function sendErrorReport(error, info) {
  try {
    await networkWithErrorConvert({
      url: '/v1/frontend-error',
      method: 'put',
      data: {
        info,
        error: {
          name: error?.name,
          message: error?.message,
          stack: error?.stack,
        },
        timestamp: new Date().toString(),
      },
    });
  } catch (err) {
    console.error(err);
  }
}

export const clustersKey = ['clusters'];

const updateClustersQuery = (fn) => () => {
  queryClient[fn](clustersKey);
  queryClient.invalidateQueries(['user-capacity']);
};

export const useClusterCreation = (axiosConfigBuilder, updateClusters) =>
  useMutation(
    (mutationParam) =>
      networkWithErrorConvert(axiosConfigBuilder(mutationParam)),
    {
      onMutate: () => {
        queryClient.setQueryData(clustersKey, (clustersList) =>
          updateClusters(clustersList)
        );
      },
      onSettled: updateClustersQuery('invalidateQueries'),
    }
  );

const updatingClusterState = (state) => (param) => {
  queryClient.setQueryData(['cluster', param], (clusterInfo) => ({
    ...clusterInfo,
    state,
  }));
};

export const useClusterMutation = (axiosConfigBuilder) =>
  useMutation(
    (mutationParam) =>
      networkWithErrorConvert(axiosConfigBuilder(mutationParam)),
    {
      onMutate: updatingClusterState('updating'),
      onSettled: (_, __, param) => {
        queryClient.invalidateQueries(['cluster', param]);
      },
    }
  );

export const useClusterDeletion = (axiosConfigBuilder) =>
  useMutation(
    (mutationParam) =>
      networkWithErrorConvert(axiosConfigBuilder(mutationParam)),
    {
      onMutate: updatingClusterState('removing'),
      onSettled: updateClustersQuery('resetQueries'),
    }
  );
